<script lang="ts" setup>
  import { useMutation } from '@tanstack/vue-query'

  import { useOrderService } from '~/core/services'
  import type { Booking, CartSoldProduct } from '~/core/types/order'

  import BookingProduct from './BookingProduct.vue'

  type Props = {
    booking: Booking | CartSoldProduct
    hasAdditionalProducts?: boolean
    light?: boolean
    slim?: boolean
  }

  const props = defineProps<Props>()
  const { updateCart } = useCartStore()
  const router = useRouter()
  const route = useRoute()

  const { deleteBooking, deleteSoldProduct } = useOrderService()

  const { mutate: deleteItemInCart } = useMutation({
    mutationFn: 'slot' in props.booking ? deleteBooking : deleteSoldProduct,
    onSuccess: async (res) => {
      if (route.path.includes('shopping-cart') && !res?.data?.bookings.length && !res?.data?.sold_products.length) {
        await router.push({ name: 'shopping-cart-basket' })
      }
      updateCart(res?.data ?? null)
    },
  })

</script>

<template>
  <div
    class="c-cart-product"
    :class="{ light: props.light }"
  >
    <div class="main">
      <BookingProduct
        :booking="booking"
        :light="light"
        :slim="slim"
        :is-greee-fee="'slot' in props.booking"
      />
      <div class="promotions-prices">
        <div
          v-if="'slot' in props.booking"
          class="promotions"
        >
          <SvgIcon
            v-if="props.booking.slot.has_promotions"
            icon="promotion"
            :width="20"
            :height="20"
            :desktop-width="25"
            :desktop-height="25"
          />
          <span
            v-if="props.booking.slot.has_promotions"
            class="name"
          >
            {{ props.booking.slot.promotion_title }}
          </span>
          <SvgIcon
            v-if="props.booking.slot.is_damaged"
            icon="cone"
            :width="20"
            :height="20"
            :desktop-width="25"
            :desktop-height="25"
          />
          <span
            v-if="props.booking.slot.is_damaged"
            class="name"
          >{{ props.booking.slot.damaged_title }}</span>
        </div>
        <div v-else />
        <div class="prices">
          <div
            v-if="booking.base_price !== booking.final_price"
            class="original"
          >
            <span class="price">{{ `${booking.base_price.toFixed(2)}€` }}</span>
            <span
              v-if="'slot' in booking && booking.slot.percentage"
              class="percentage"
            >-{{ Math.round(booking.slot.percentage) }}%</span>
          </div>
          <div class="final">
            <span>{{ `${(booking.final_price - ('slot' in booking ? booking.fees : 0)).toFixed(2)}€` }}</span>
          </div>
          <div
            class="action"
            @click="() => deleteItemInCart(booking.id)"
          >
            <SvgIcon
              icon="trash"
              :desktop-width="24"
              :desktop-height="28"
              :width="20"
              :height="22"
            />
          </div>
        </div>
      </div>
      <p
        v-if="'golf_price' in booking && booking.golf_price"
        class="golf-price"
      >
        Le reste du montant doit être à payer au golf ({{ `${booking.golf_price?.toFixed(2)}€` }})
      </p>
    </div>
    <div
      v-if="'slot' in props.booking && hasAdditionalProducts"
      class="additional-products"
    >
      <slot name="additional-products" />
    </div>
    <div class="dropdown">
      <slot name="dropdown" />
    </div>
  </div>
</template>

<style scoped>

.c-cart-product {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: space-between;

  .main {
    display: flex;
    width: 100%;
    flex-direction: column;
    padding: 1.5em;
    background-color: white;

    .promotions-prices {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .promotions {
        display: flex;
        align-items: center;
        margin-top: 0.5em;

        .c-icon {
          margin-right: 0.5em;
        }

        .name {
          color: var(--color-promo);
          font-family: Bariol, sans-serif;
          font-size: 0.875em;
          font-weight: normal;
          opacity: 1;
        }
      }

      .prices {
        display: flex;
        flex-direction: row;
        align-items: center;

        .original {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin: 0 0.375em 0 0;
          line-height: 0.875em;

          .price {
            color: var(--color-grey-light);
            font-family: Bariol, sans-serif;
            font-size: 0.875em;
            font-weight: normal;
            text-decoration: line-through;
          }

          .percentage {
            color: var(--color-promo);
            font-family: Open-Sans, sans-serif;
            font-size: 0.875em;
            font-weight: 500;
            white-space: nowrap;
          }
        }

        .final {
          margin-right: 0.5em;
          color: var(--color-primary);
          font-family: Bariol, sans-serif;
          font-size: 1.625em;
          font-weight: bold;
        }

        .action {
          display: flex;
          flex-direction: row;
          align-items: flex-end;
          margin: auto 0;
          cursor: pointer;
        }
      }
    }

    .golf-price {
      color: var(--color-grey-light);
      font-family: Bariol, sans-serif;
      font-size: 0.875em;
      font-weight: normal;
      text-align: right;
    }
  }

  .additional-products {
    padding: 0.625em 1.5em 3em;
    margin-top: 0.125em;
    background-color: white;
  }

  .dropdown {
    margin: 0 0.75em;
    transform: translateY(-1.5em);
  }

  &.light {
    .content {
      .description {
        .name-date {
          margin: 2em 0 0;
        }
      }
    }

    .main {
      position: relative;
      padding-bottom: 3em;

      .promotions-prices {
        right: 0;
        bottom: 2.5em;
        justify-content: flex-end;
        margin-top: 0;

        .promotions {
          .name {
            display: none;
          }
        }

        .prices {
          .action {
            display: none;
          }
        }
      }
    }

    .additional-products {
      display: none;
    }
  }

  &.header {
    .main {
      padding: 0 1.5em 1.5em 0;
    }
  }

  @media screen and (width >= 1024px) {
    .main {
      padding: 2.5em;

      .promotions-prices {
        .promotions {
          .name {
            font-size: 1em;
          }
        }

        .prices {
          .original {
            font-size: 1.25em;
          }

          .final {
            font-size: 2.5em;
          }

          .action {
            margin: 0.5em 0;
          }
        }
      }
    }

    .additional-products {
      padding: 0.875em 2.5em 2.5em;
    }

    .dropdown {
      margin: 0 3em;
    }

    &.header {
      .main {
        padding: 0 1em 0 0;

        .promotions-prices {
          .prices {
            .original {
              font-size: 1.125em;
            }

            .final {
              font-size: 2.125em;
            }

            .action {
              margin: 0.5em 0;
            }
          }
        }
      }
    }
  }
}
</style>
